import { formattedTypeContainer } from "../../../components/formatted/FormattedText";

export const getDefaultActivityPeriod = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(today);
  start.setMonth(start.getMonth() - 6);
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  return {
    periodFilter: 6,
    periodHistoryStart: start.toISOString(),
    periodHistoryStop: end.toISOString(),
  };
};

// Default period for the containers list "Historique" tab (ActivityHistory),
// kept separate from the main Activity page's 6-month default above.
export const getDefaultContainerHistoryPeriod = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(today);
  start.setDate(start.getDate() - 30);
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  return {
    periodFilter: 1,
    periodHistoryStart: start.toISOString(),
    periodHistoryStop: end.toISOString(),
  };
};

export const getOptionValueId = (option) => `${option.type}-${option.key}`;

export const formatKeywordOptionLabel = (option) => {
  if (option.type === "type") {
    return formattedTypeContainer(option.key);
  }
  if (option.type === "id") {
    const zoneLabel = option.name || option.key || "-";
    const capitalizedZone =
      zoneLabel === "-"
        ? zoneLabel
        : zoneLabel.charAt(0).toUpperCase() + zoneLabel.slice(1);
    const addressLabel = (option.address || option.addressName || "").trim();
    // Backend may already decorate as "Zone - Site"; avoid duplicating the site.
    if (
      addressLabel &&
      addressLabel !== "-" &&
      !capitalizedZone.toLowerCase().includes(addressLabel.toLowerCase())
    ) {
      return `${capitalizedZone} - ${addressLabel}`;
    }
    return capitalizedZone;
  }
  return option.name || option.key || "-";
};

const sortOptionsByLabel = (options) =>
  [...options].sort((optionA, optionB) =>
    (optionA.label || "").localeCompare(optionB.label || "", undefined, {
      sensitivity: "base",
    }),
  );

export const groupKeywordFilterOptions = (keywordFilterOptions = []) => {
  const groups = keywordFilterOptions
    .filter((option) => option.key !== null && option.key !== undefined)
    .reduce(
      (accumulator, option) => {
        const formattedOption = {
          ...option,
          value: getOptionValueId(option),
          label: formatKeywordOptionLabel(option),
        };

        if (option.type === "id") {
          accumulator.zone.push(formattedOption);
        } else if (option.type === "waste") {
          accumulator.flux.push(formattedOption);
        } else if (option.type === "address") {
          accumulator.address.push(formattedOption);
        }

        accumulator.byValue[formattedOption.value] = option;
        return accumulator;
      },
      { zone: [], flux: [], address: [], byValue: {} },
    );

  groups.zone = sortOptionsByLabel(groups.zone);
  groups.flux = sortOptionsByLabel(groups.flux);
  groups.address = sortOptionsByLabel(groups.address);

  return groups;
};

export const buildKeywordFilterValue = ({
  zoneFilter,
  fluxFilter,
  addressFilter,
  optionsByValue,
}) => {
  const value = [zoneFilter, fluxFilter, addressFilter]
    .filter((filterValue) => filterValue && filterValue !== "all")
    .map((filterValue) => optionsByValue[filterValue])
    .filter(Boolean);

  return { value };
};

export const getFilterLabel = (options, filterValue, allLabel) => {
  if (!filterValue || filterValue === "all") {
    return allLabel;
  }

  return (
    options.find((option) => option.value === filterValue)?.label ?? allLabel
  );
};

export const syncKeywordFiltersFromValue = (keywordFilter) => {
  const synced = {
    zoneFilter: "all",
    fluxFilter: "all",
    addressFilter: "all",
  };

  (keywordFilter?.value || []).forEach((option) => {
    const valueId = getOptionValueId(option);
    if (option.type === "id") synced.zoneFilter = valueId;
    else if (option.type === "waste") synced.fluxFilter = valueId;
    else if (option.type === "address") synced.addressFilter = valueId;
  });

  return synced;
};

export const buildHistoricalFluxFilterOptions = (historicalFluxLabels = []) =>
  sortOptionsByLabel(
    [
      ...new Set(
        (historicalFluxLabels || [])
          .filter((label) => typeof label === "string" && label.trim())
          .map((label) => label.trim()),
      ),
    ].map((label) => {
      const option = {
        type: "waste",
        key: label,
        name: label,
        cat: "Flux",
      };
      return {
        ...option,
        value: getOptionValueId(option),
        label: formatKeywordOptionLabel(option),
      };
    }),
  );

export const buildHistoricalOriginFilterOptions = (
  historicalOriginLabels = [],
) =>
  sortOptionsByLabel(
    [
      ...new Set(
        (historicalOriginLabels || [])
          .filter((label) => typeof label === "string" && label.trim())
          .map((label) => label.trim()),
      ),
    ].map((label) => ({ value: label, label })),
  );

export const withHistoricalFluxOptions = (
  groupedOptions,
  historicalFluxLabels = [],
) => {
  const flux = buildHistoricalFluxFilterOptions(historicalFluxLabels);
  const byValue = { ...(groupedOptions?.byValue || {}) };

  Object.keys(byValue).forEach((valueId) => {
    if (byValue[valueId]?.type === "waste") {
      delete byValue[valueId];
    }
  });

  flux.forEach((option) => {
    byValue[option.value] = {
      type: option.type,
      key: option.key,
      name: option.name,
      cat: option.cat,
    };
  });

  return {
    zone: groupedOptions?.zone || [],
    address: groupedOptions?.address || [],
    flux,
    byValue,
  };
};
